import {
    HeartbeatUpdateAfter,
    UNGROUPED_DISPLAY_NAME,
    UNGROUPED_NAME,
} from "constant";
import {
    DEACTIVATE,
    GatewayType,
    DeviceStatus,
    NetworkOBJ,
} from "constant/gateway";
import {
    flatten,
    forEach,
    get,
    isEmpty,
    isNumber,
    sortBy,
    uniqBy,
} from "lodash";
import { Col } from "react-bootstrap";
import {
    getGatewayAlertNotification,
    getGwAndLDSUStatus,
    getRegistryGroups,
    transformDeviceList,
} from "service/gatewayService";
import localStorageService from "service/localStorageService";
import { GatewayObj, MemberDto } from "types";
import {
    LdsActuator,
    LdsActuatorDetails,
    ActuatorCommand,
} from "types/Actuator";
import {
    Device,
    GatewayDeviceList,
    GatewayReadingDevice,
    PortStatus,
    SensorTypeObject,
    Gateway,
    Registry,
    DeviceConfig,
    Group,
} from "types/Gateways";
import { CombinedLdsu, LdsApp, LDSU_STATUS, LdsuDto } from "types/Ldsu";
import { dateTimeFormatter } from "utils/dateTimeFormatter";
import {
    clsToAttributeTable,
    getPrimaryUnit,
    getPrimaryUnitKey,
    getPrimaryUnitName,
    isActuator,
    isHttpSuccess,
    isInteger,
} from "utils/functions";
import { formatReadingValue } from "./chart";

/**
 * Render activity status for gateway
 * If the difference between current time and heartbeat is > 120 (2 minutes), render offline
 * @param { number } heartbeat - unix timestamp
 * @param { boolean } info - status of information
 * @return { JSX } - Time is included if it is online
 */

export const checkActive = (heartbeat: number, info: boolean) => {
    if (info) {
        // Heartbeat exists
        // Get user current unixtime
        const timestamp = Math.round(Date.now() / 1000);

        const difference = timestamp - heartbeat;
        const offlineStatus =
            dateTimeFormatter.display.offlineStatus(heartbeat);

        return (
            <>
                <i
                    className={`material-icons circle ${difference < HeartbeatUpdateAfter ? "online" : "offline"
                        }`}
                >
                    circle
                </i>{" "}
                {difference < HeartbeatUpdateAfter
                    ? `Online`
                    : `Offline from ${offlineStatus}`}
            </>
        );
    }
    return (
        // No info means it is not onboarded
        <>
            <i className="material-icons circle offline">circle</i> Not
            On-boarded
        </>
    );
};

export const getDeviceStatus = (
    heartbeat: number,
    hasInfo: boolean,
    timeToCheck: number = Date.now()
) => {
    if (!hasInfo) {
        return DeviceStatus.NotFound;
    }

    const timestamp = Math.round(timeToCheck / 1000);
    const difference = timestamp - heartbeat;

    if (difference < HeartbeatUpdateAfter) {
        return DeviceStatus.Online;
    }

    return DeviceStatus.Offline;
};

/**
 * Render activity status for gateway
 * If the difference between current time and heartbeat is > 120 (2 minutes), render offline
 * @param { number } heartbeat - unix timestamp
 * @param { boolean } info - status of information
 * @return { JSX } - Time is not included.
 */
export const checkActiveStatus = (
    heartbeat: number,
    info: boolean,
    referenceTimestamp?: number
) => {
    if (info) {
        // Heartbeat exists
        // Get reference unixtime: use provided reference (e.g., last refresh click) or fallback to current time
        const timestamp = referenceTimestamp ?? Math.round(Date.now() / 1000);
        const difference = timestamp - heartbeat;
        return (
            <>
                <i
                    className={`material-icons circle ${difference < HeartbeatUpdateAfter ? "online" : "offline"
                        }`}
                >
                    circle
                </i>{" "}
                {difference < HeartbeatUpdateAfter ? "Online" : "Offline"}
            </>
        );
    }
    return (
        // No info means it is not onboarded
        <Col md={{ offset: 0, order: 4 }} xs={{ span: 10, order: 6 }}>
            <i className="material-icons circle offline">circle</i> Not
            On-boarded
        </Col>
    );
};

export const getSensorTypes = (devices: Device[] = []): SensorTypeObject[] => {
    const devicesTypes: SensorTypeObject[] = devices.reduce(
        (acc: any, d: any) => {
            let types = {
                id: d.sensorType ?? d.sensor_type,
                name: d.sensorType ?? d.sensor_type,
            };

            if (d.APP) {
                types.id = d.APP[d.SAID].NAME;
                types.name = d.APP[d.SAID].NAME;
            }

            acc.push(types);
            return acc;
        },
        []
    );

    return sortBy(uniqBy(devicesTypes, "name"), "name");
};

export const fetchGWStatus = async (gatewayId: string) => {
    const res: any = await getGwAndLDSUStatus(gatewayId);

    if (res.status === 200) {
        const { heartbeat = null } = res.data;
        if (heartbeat) {
            const timestamp = Math.round(Date.now() / 1000);
            const difference = timestamp - heartbeat;
            return difference < HeartbeatUpdateAfter;
        }
    }
    return false;
};

const getLdsuList = (
    registryInfoList: LdsuDto[],
    configList: DeviceConfig[],
    gwLDSUStatusList: GatewayReadingDevice[]
) => {
    const combined: Record<string, CombinedLdsu> = {};
    [registryInfoList, configList, gwLDSUStatusList].forEach((arr) => {
        arr.forEach((item: LdsuDto | DeviceConfig | GatewayReadingDevice) => {
            if (combined[item.UID]) {
                Object.assign(combined[item.UID], item);
            } else {
                combined[item.UID] = item;
            }
        });
    });

    return uniqBy(Object.values(combined), "uuid");
};

const getSensorList = (ldsuList: CombinedLdsu[], jsonList: any) => {
    let sensorFilteredLst: any = [];

    ldsuList.forEach((i: CombinedLdsu) => {
        jsonList.forEach((j: any) => {
            if (i.OBJ === j.OBJ && i.SAIDS) {
                for (let k = 0; k < i?.SAIDS?.length; k++) {
                    // Could be (1)IO controller or (2) sensor

                    // === IF it's IO controller sensor && has been configured ===
                    if (i.APP && !isEmpty(i.APP[k]) && j.SNS[k]?.CLS < 32768) {
                        const defaultUnit = getPrimaryUnitName(i.APP[k]);
                        let body: any = {
                            ldsu_uuid: i.UID,
                            ldsu_name: i.name,
                            SAID: k,
                            app_id: i.APP[k]?.APPID,
                            obj: i.OBJ,
                            cls: j.SNS[k].CLS,
                            sensor_name: i.sensor_names?.[k],
                            sensor_type: i.APP[k]?.NAME,
                            primaryUnitKey: getPrimaryUnitKey(i.APP[k]),
                            unit: getPrimaryUnit(i.APP[k]),
                            minmax: [defaultUnit.MIN, defaultUnit.MAX],
                            accuracy: defaultUnit.ACCURACY,
                        };

                        sensorFilteredLst.push(body);
                        // === IF it's sensor ===
                    } else if (!i.APP && j.SNS[k]?.CLS < 32768) {
                        let body: any = {
                            ldsu_uuid: i.UID,
                            ldsu_name: i.name,
                            SAID: k,
                            MODE: i.MODES?.[k],
                            obj: i.OBJ,
                            cls: j.SNS[k].CLS,
                            sensor_name: i.sensor_names?.[k],
                            sensor_type: get(
                                clsToAttributeTable(),
                                j.SNS[k]?.CLS
                            ),
                            unit: j.SNS[k]?.UNIT,
                            minmax: [j.SNS[k]?.MIN, j.SNS[k]?.MAX],
                            accuracy: j.SNS[k]?.ACCURACY
                                ? j.SNS[k]?.ACCURACY
                                : j.SNS[k]?.MODE?.[0]?.ACCURACY,
                        };
                        // If the sensor has opmode
                        if (j.SNS[k]?.MODE) {
                            body["opmodes"] = [j.SNS[k]?.MODE[0]];
                        }

                        sensorFilteredLst.push(body);
                    }
                }
            }
        });
    });
    return sensorFilteredLst;
};

const containsActuatorObject = (objId: string) => {
    const hex = Number.parseInt(objId).toString(16).toUpperCase();
    // NOTE: 0100 - 4 (actuator), 1000 - 8 (sensor), 1100 - C (actuator & sensor)
    return ["4", "C"].includes(hex[0]);
};

const getActuatorList = (
    registryInfoList: LdsuDto[],
    configList: DeviceConfig[],
    gwLDSUStatusList: GatewayReadingDevice[],
    jsonList: any,
    busNumber: string
) => {
    let actuatorFilteredLst: LdsActuator[] = [];

    let extractedActuatorLst = registryInfoList.filter((i: LdsuDto) =>
        containsActuatorObject(i.OBJ)
    );

    extractedActuatorLst.forEach((i: LdsuDto) => {
        configList.forEach((j: DeviceConfig) => {
            gwLDSUStatusList?.forEach((k: GatewayReadingDevice) => {
                if (
                    i.UID === j.UID &&
                    i.PORT === busNumber &&
                    i.UID === k?.UID
                ) {
                    const ldsuName = (i as any).name || i.NAME || "";
                    let body: any = {
                        UID: i.UID,
                        OBJ: i.OBJ,
                        SAID: j.SAIDS,
                        sensor_name: j.sensor_names,
                        CMDS: j.CMDS,
                        timestamp: k.ts,
                        status: k.value,
                        APP: i.APP,
                        // Carry LDSU name through to actuator objects so details pages
                        ldsu_name: ldsuName,
                    };
                    actuatorFilteredLst.push(body);
                }
                return null;
            });
        });
    });

    const filteredJsonList = jsonList.filter((j: any) => {
        for (let k of j.SNS) {
            // CLS >= 32768 are actuators, filter out 256 (old sensor not removed by BE yet)
            if (Number(k.CLS) >= 32768 && j.OBJ !== "256") {
                return true;
            }
        }

        return false;
    });

    let allActuators: LdsActuatorDetails[] = [];
    for (let a of actuatorFilteredLst) {
        for (let j of filteredJsonList) {
            if (a.OBJ === j.OBJ) {
                let actuatorIndex = [];

                for (let k = 0; k < j.SNS.length; k++) {
                    if (Number(j.SNS[k]?.CLS) >= 32768) {
                        // Actuators can be (1) IO controller actuators, (2) other actuators

                        // === IO controller ===

                        if (a.APP && !isEmpty(a.APP[k])) {
                            actuatorIndex.push({
                                index: k,
                                type: a.APP[k]?.NAME,
                                appId: a.APP[k]?.APPID,
                            });
                        } else if (!a.APP) {
                            // === actuators ===
                            actuatorIndex.push({
                                index: k,
                                type: get(clsToAttributeTable(), j.SNS[k]?.CLS),
                            });
                        }
                    }
                }
                let CMDS: ActuatorCommand[] = [];
                for (let i of actuatorIndex) {
                    CMDS.push(a.CMDS[i.index]);
                    allActuators.push({
                        ...a,
                        CMDS,
                        name: a.sensor_name[i.index],
                        type: i.type,
                        info: a.CMDS[i.index],
                        said: i.index,
                        CLS: j.SNS[i.index].CLS,
                        appId: i.appId,
                    });
                }
            }
        }
    }
    return allActuators;
};

export const getLDSBusInfo = (
    busNumber: string,
    registryInfoList: LdsuDto[],
    configList: DeviceConfig[],
    gwLDSUStatusList: GatewayReadingDevice[]
) => {
    let jsonList = localStorageService.getItem("listOfJSON");

    const ldsuList = getLdsuList(
        registryInfoList,
        configList,
        gwLDSUStatusList
    ).filter((l: any) => l.PORT === busNumber);

    const sensorList = getSensorList(ldsuList, jsonList);

    const actuatorList = getActuatorList(
        registryInfoList,
        configList,
        gwLDSUStatusList,
        jsonList,
        busNumber
    );

    return {
        ldsus: ldsuList,
        sensors: sensorList,
        actuators: actuatorList,
    };
};

export const fetchGWStatuses = async (gatewayIdArr: any = []) => {
    const unresolvedStatuses: any = gatewayIdArr.map(async (g: any) => {
        return await fetchGWStatus(g);
    });
    const statuses = await Promise.all(unresolvedStatuses);
    return statuses;
};

export const formatReading = (reading: number, accuracy: number) => {
    if (reading === undefined) {
        return "-";
    } else if (isNumber(reading) && isInteger(accuracy)) {
        return reading.toFixed(accuracy);
    }
    return reading;
};

export const decimalPlaceMatches = (reading: any, acceptedDecimal: any) => {
    return (
        (reading.toString().split(".")[1]?.length || 0) <=
        Number(acceptedDecimal)
    );
};

export const extractedIdList = (resource: MemberDto[] = []) => {
    return resource.map((member) => member.username);
};

export const convertIdToUserInfoArray = (
    orgList: MemberDto[],
    resourceList: string[]
) => {
    return orgList.reduce((acc: MemberDto[], member: MemberDto) => {
        if (resourceList.includes(member.username)) {
            acc.push(member);
        }
        return acc;
    }, []);
};

export const getPowerStatusText = (power: boolean, status: PortStatus) => {
    let powersStatus = power ? "On" : "Off";

    if (isFaulty(status)) {
        powersStatus = "Faulty";
    }

    return powersStatus;
};

export const isFaulty = (status: PortStatus = PortStatus.On) => {
    return [
        PortStatus.FaultyAll,
        PortStatus.FaultyHardware,
        PortStatus.FaultyIndividual,
    ].includes(status);
};

export function isStandaloneGroup(group: Group): boolean {
    return get(group, "name") === UNGROUPED_NAME && !group.uuid;
}

export const fetchDevices = async (): Promise<GatewayDeviceList> => {
    const result = {} as GatewayDeviceList;

    const registryRes = await getRegistryGroups();

    if (isHttpSuccess(registryRes.status)) {
        const gateWayList: any[] = [];
        const data = get(registryRes, "data", []);
        forEach(data, (group) => {
            const groupName = isStandaloneGroup(group)
                ? UNGROUPED_DISPLAY_NAME
                : get(group, "name", UNGROUPED_DISPLAY_NAME);
            get(group, "gateways", []).forEach((gateway: any) => {
                gateWayList.push({
                    ...gateway,
                    groupName,
                });
            });
        });

        result.allRegistry = gateWayList.map((gateway) => {
            return {
                ...gateway,
                gatewayType: getGatewayType(gateway.info),
            };
        });

        const deviceListRes = await Promise.all(
            result.allRegistry.map((registry) =>
                transformDeviceList(
                    registry.info?.devices ?? [],
                    registry.configs ?? [],
                    {
                        name: registry.name ?? "",
                        gateway_id: registry.gateway_id ?? "",
                    }
                )
            )
        );

        const responses = await Promise.all(
            result.allRegistry.map((registry) =>
                getGatewayAlertNotification(registry.gateway_id)
            )
        );

        const deviceAlerts = flatten(
            responses.map((response) => get(response, "data", []))
        );
        const jsonInfo = localStorageService.getItem("listOfJSON");

        result.deviceList = flatten(deviceListRes ?? []).map((device: any) => {
            let device_unit = "";

            if (device.APP) {
                const appSensor = device.APP.find(
                    (d: any) => d.APPID === device.APPID
                );
                device_unit = getPrimaryUnit(appSensor) ?? "";
            } else {
                device_unit = device.unit;
            }
            const infoJson = jsonInfo.find(
                (json: any) => json.OBJ === device.OBJ
            );
            const said = device.SAID;
            const infoJsonDevice = infoJson?.SNS?.find(
                (json: any) => json.SAID === String(said)
            );

            const mode = get(device, `MODES.${said}`);
            const app = device?.APP ? device.APP[said] : null;

            const min = getValueModeOrObj(infoJsonDevice, app, "MIN", mode);
            const max = getValueModeOrObj(infoJsonDevice, app, "MAX", mode);
            const format = getValueModeOrObj(
                infoJsonDevice,
                app,
                "FORMAT",
                mode
            );
            const accuracy = getValueModeOrObj(
                infoJsonDevice,
                app,
                "ACCURACY",
                mode
            );
            const { gatewayName, bus, gatewayId } = device;

            const deviceGateway = result.allRegistry.find(
                (g) => g.gateway_id === gatewayId
            );
            const busLabel =
                deviceGateway?.gatewayType === GatewayType.LoRaWAN
                    ? "LoRaWAN"
                    : `LDSBUS ${bus}`;

            const deviceAlert = deviceAlerts.find(
                (alert) =>
                    device.ldsu_uuid === alert.device_id &&
                    device.SAID === alert.said
            );
            const alertAvailable = !!deviceAlert?.enabled;

            return {
                deviceId: device.ldsu_uuid + "/" + device.SAID,
                app: device.APP,
                appId: device.APPID,
                ldsuUID: device.ldsu_uuid,
                gatewayName: device.gatewayName,
                said: device.SAID,
                name: device.name,
                deviceName: device.product_nick,
                deviceType: device.device_type,
                sensorName: device.device_name,
                sensorType: device.sensor_type,
                bus: device.name,
                cls: device.CLS,
                gatewayId: device.gatewayId,
                unit: device_unit,
                min: Number(min),
                max: Number(max),
                accuracy: Number(accuracy),
                mode: device.mode,
                modes: device.MODE,
                obj: device.OBJ,
                intvl: device.INTVL,
                format: format,
                busName: `${gatewayName} - ${busLabel}`,
                isActuator: isActuator(device.CLS),
                alertAvailable,
            };
        });
    }

    return result;
};

export const addOrRemoveConfirmLocationPopup = (
    gatewayId: string,
    isAdd = true
) => {
    const showConfirmLocationPopup =
        localStorageService.getItem("showConfirmLocationPopup") || {};

    localStorageService.setItem("showConfirmLocationPopup", {
        ...showConfirmLocationPopup,
        [gatewayId]: isAdd ? true : undefined,
    });
};

export const isShowLocationPopup = (gatewayId: string) => {
    const showConfirmLocationPopup =
        localStorageService.getItem("showConfirmLocationPopup") || {};

    return showConfirmLocationPopup[gatewayId];
};

export const getGatewayType = (gateway: Gateway): GatewayType | null => {
    const gatewayModel = get(gateway, "model", "");
    const gatewayObject = get(gateway, "OBJ") as GatewayObj;

    const modelMapping: Record<string, GatewayType> = {
        LG010201A: GatewayType.PSU,
        LG010101A: GatewayType.POE,
    };

    const objectMapping: Partial<Record<GatewayObj, GatewayType>> = {
        PCS_GW: GatewayType.PCS,
        "LORAWAN-GW": GatewayType.LoRaWAN,
    };

    if (modelMapping[gatewayModel]) {
        return modelMapping[gatewayModel];
    }

    return objectMapping[gatewayObject] || null;
};

/**
 * This function used to check if the gateway is a LoRaWAN gateway or not
 * @param { Registry } registry - The registry object
 * @return { boolean } - True if the gateway is a LoRaWAN gateway, false otherwise
 */
export const isLoRaWanGateway = (registry?: Registry): boolean => {
    if (!registry || isEmpty(registry)) {
        return false;
    }

    if (registry.info) {
        return getGatewayType(registry.info) === GatewayType.LoRaWAN;
    }

    return isLoRaWanGateway_ByGatewayId(registry.gateway_id ?? "");
};

export const getValueModeOrObj = (
    obj: any,
    app: LdsApp | null,
    valueName: string,
    opMode: number
) => {
    if (app) {
        const defaultUnit = getPrimaryUnitName(app);

        return defaultUnit[valueName];
    }

    const modeValue = get(obj, `${valueName}`),
        objValue = get(obj, `MODE`)?.find(
            ({ ID }: { ID: string }) => ID === opMode.toString()
        )?.[valueName];
    return modeValue === undefined ? objValue || "" : modeValue;
};

export const isLoRaWanGateway_ByGatewayId = (gateway_id: string): boolean => {
    return gateway_id.startsWith("LW");
};

export const isNetworkGateway = (registry?: Partial<Registry>): boolean => {
    if (!registry || isEmpty(registry)) {
        return false;
    }

    const gatewayId = registry.gateway_id ?? (registry as any).gatewayId ?? "";
    return registry.info?.OBJ === NetworkOBJ || gatewayId.startsWith("PN0");
};

export const getUngroupedForNewGroup = (groupList: Group[]): Group | null => {
    // Used find because there is only one group with name "ungrouped"
    const standaloneGroup = groupList.find((group: Group) =>
        isStandaloneGroup(group)
    );

    if (!standaloneGroup) {
        return null;
    }

    // Remove the PCS gateways from the standalone group
    if (standaloneGroup.gateways) {
        standaloneGroup.gateways = standaloneGroup.gateways.filter(
            (gateway: any) => getGatewayType(gateway.info) !== GatewayType.PCS
        );
    }

    // Remove on-boarded and not on-boarded LoRaWAN gateways from the standalone group
    if (standaloneGroup.gateways) {
        standaloneGroup.gateways = standaloneGroup.gateways.filter(
            (gateway: Registry) => !isLoRaWanGateway(gateway)
        );
    }

    return standaloneGroup;
};

export const getActuatorState = ({
    isFourWired,
    activation_state,
    polarity,
}: {
    isFourWired: boolean;
    activation_state: string;
    polarity: boolean;
}) => {
    if (!isFourWired) return activation_state;

    if (activation_state === DEACTIVATE) return "Pause";
    return polarity ? "Forward" : "Reverse";
};

// Count sensors and actuators from configs
export const countSensorsAndActuators = (gateway: any) => {
    let sensorCount = 0;
    let actuatorCount = 0;
    const jsonList = localStorageService.getItem("listOfJSON");

    if (!gateway?.info) {
        return null;
    }

    const configs = gateway?.configs || [];
    const devices = gateway?.info.devices || [];

    devices.forEach((devices: any) => {
        const configFromJson = (jsonList ?? []).find(
            (j: any) => j.OBJ === devices.OBJ
        );
        const deviceConfig = configs.find((c: any) => c.UID === devices.UID);
        const saidsConfig = deviceConfig?.SAIDS || [];

        if (!configFromJson) {
            return;
        }

        for (let saidIndex = 0; saidIndex < saidsConfig.length; saidIndex++) {
            const currentItem = configFromJson.SNS[saidIndex];
            if (!currentItem?.CLS) {
                continue;
            }

            const cls = Number(currentItem.CLS);
            // Check if it's an IO controller and configured or not
            const isIOController = !!devices.APP;
            const isConfigured = !isEmpty(devices.APP?.[saidIndex]);

            // CLS >= 32768 are actuators
            if (cls >= 32768) {
                /**
                 * Only count actuators that are either:
                 * 1. IO controller with configuration
                 * 2. Regular actuators (non-IO controller)
                 */
                if (!isIOController || (isIOController && isConfigured)) {
                    actuatorCount++;
                }
            } else if (!isIOController || (isIOController && isConfigured)) {
                /**
                 * Only count sensors that are either:
                 * 1. IO controller with configuration
                 * 2. Regular sensors (non-IO controller)
                 */

                sensorCount++;
            }
        }
    });

    return {
        sensorCount,
        actuatorCount,
        ldsuCount: devices.length,
    };
};

export const getLDSUStatusColor = (status: string) => {
    if (status === LDSU_STATUS.REACHABLE) {
        return "#219653";
    } else if (status === LDSU_STATUS.NOT_REACHABLE) {
        return "#f2495e";
    } else {
        return "#E39B17";
    }
};

/**
 * Format the reading2 value for display. Callers should show "-" when the API returned no usable data.
 * @return { string } - The current value to display
 */
export const getCurrentValueToDisplay = (
    value: number,
    _timestamp: number,
    accuracy: number | null,
    unit?: string) => {
    return formatReadingValue(value, accuracy, unit).toString();
}

export const getReadingDateTimeToDisplay = (
    timestamp: number | null) => {
    if (!timestamp) return "-";

    return dateTimeFormatter.dateTime(timestamp * 1000, true);
}
export const isRoomGroupZoneDevice = (device: Device): boolean => {
    const deviceType = device.deviceType?.toLowerCase().trim();
    // deviceType is coming from fetchDevices function device_type. 
    // device_type if coming from transformDeviceList and its device.product_name
    // produce_name is device.NAME which is room,group,zone,hub
    return deviceType === "room" || deviceType === "group" || deviceType === "zone" || deviceType === "hub";
};

export const isRoomGroupZoneHub_ByObj = (obj: string): boolean => {
    const objValue = Number(obj);
    return objValue === 45057 || objValue === 45058;
};

// Utility function to extract filename from content-disposition header
export const extractDownloadFilename = (headers: any): string => {
    const contentDisposition =
        get(headers, "content-disposition") ||
        get(headers, "Content-Disposition") ||
        get(headers, ["headers", "content-disposition"]) ||
        get(headers, ["headers", "Content-Disposition"]);

    if (!contentDisposition) {
        return "download";
    }

    const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );

    return fileNameMatch?.[1]?.replaceAll(/['"]/g, "") || "download";
};
